import { useState, useEffect, useCallback } from "react";
import { API, axios } from "@/api";
import {
  Loader2, Pencil, Check, X, ChevronDown, ChevronRight, Database,
} from "lucide-react";
import { toast } from "sonner";

const FIELD_CONFIG = {
  offer_title: { label: "Offer Title", type: "text" },
  offer_summary: { label: "Summary", type: "text" },
  offer_value_est: { label: "Value ($)", type: "number" },
  offer_window_days: { label: "Window (days)", type: "number" },
  offer_redemption_channel: { label: "Channel", type: "select", options: ["In-Store", "Online", "App", "Both"] },
  offer_reward_type: { label: "Reward Type", type: "select", options: ["Free Item", "Discount", "BOGO", "Points", "Gift Card", "Free Drink", "Unknown"] },
  category: { label: "Category", type: "select", options: ["Restaurant", "Fast Food", "Cafes & Drinks", "Bakery", "Dessert & Ice Cream", "Beauty & Skincare", "Clothing & Apparel", "Retail", "Entertainment", "Health & Fitness", "Grocery", "Pet", "Auto", "Home & Garden", "Other"] },
  website: { label: "Website", type: "text" },
  halal: { label: "Halal", type: "select", options: ["Yes", "No", "Unknown"] },
  vegan: { label: "Vegan", type: "select", options: ["Yes", "No", "Unknown"] },
  gluten_free_certified: { label: "GF Certified", type: "select", options: ["Yes", "No"] },
  gluten_free_friendly: { label: "GF Friendly", type: "select", options: ["Yes", "No"] },
  verification_url: { label: "Verification URL", type: "text" },
  logo_url: { label: "Logo URL", type: "text" },
  notes: { label: "Notes", type: "text" },
  min_spend: { label: "Min Spend ($)", type: "number" },
  search_tags: { label: "Search Tags", type: "text" },
  app_store_url: { label: "App Store URL", type: "text" },
  website_url: { label: "Website URL", type: "text" },
};

const InlineEdit = ({ field, config, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-red-50 border border-red-100 hover:border-red-300 transition group"
        data-testid={`dc-fill-${field}`}
      >
        <span className="text-[10px] text-red-400 font-medium">{config.label}</span>
        <Pencil className="w-2.5 h-2.5 text-red-300 group-hover:text-red-500 transition" />
      </button>
    );
  }

  const save = () => {
    if (!value && value !== 0) { setEditing(false); return; }
    const finalVal = config.type === "number" ? Number(value) : value;
    onSave(field, finalVal);
    setEditing(false);
    setValue("");
  };

  return (
    <div className="flex items-center gap-1 bg-sky-50 border border-sky-200 rounded-md px-2 py-1" data-testid={`dc-edit-${field}`}>
      <span className="text-[10px] text-sky-600 font-medium whitespace-nowrap">{config.label}:</span>
      {config.type === "select" ? (
        <select
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="text-[10px] font-bold border border-sky-300 rounded px-1 py-0.5 bg-white max-w-[120px]"
          autoFocus
        >
          <option value="">--</option>
          {config.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : config.type === "number" ? (
        <input
          type="number"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-16 text-[10px] font-bold border border-sky-300 rounded px-1 py-0.5 bg-white"
          autoFocus
          onKeyDown={(e) => e.key === "Enter" && save()}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="flex-1 min-w-[100px] text-[10px] font-bold border border-sky-300 rounded px-1 py-0.5 bg-white"
          autoFocus
          onKeyDown={(e) => e.key === "Enter" && save()}
        />
      )}
      <button onClick={save} className="text-green-500 hover:text-green-700" data-testid={`dc-save-${field}`}>
        <Check className="w-3 h-3" />
      </button>
      <button onClick={() => { setEditing(false); setValue(""); }} className="text-slate-400 hover:text-slate-600">
        <X className="w-3 h-3" />
      </button>
    </div>
  );
};

export default function DataCompletenessPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [saving, setSaving] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/data-completeness`);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFieldSave = async (businessId, businessName, field, value) => {
    setSaving(businessId);
    try {
      const res = await axios.put(`${API}/admin/business/${businessId}/fields`, { fields: { [field]: value } });
      const actionId = res.data.action_id;
      toast.success(`Updated ${FIELD_CONFIG[field]?.label || field} on ${businessName}`, {
        action: actionId ? {
          label: "Undo",
          onClick: async () => {
            try {
              await axios.post(`${API}/admin/actions/${actionId}/undo`);
              toast.success("Undone");
              fetchData();
            } catch {
              toast.error("Undo window expired");
            }
          },
        } : undefined,
        duration: 6000,
      });
      fetchData();
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(null);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-5 h-5 animate-spin text-teal-500" />
      </div>
    );
  }

  if (!data) return <p className="text-sm text-slate-400 text-center py-4">No data</p>;

  const { summary, records } = data;

  return (
    <div className="space-y-3" data-testid="data-completeness-panel">
      {/* Summary */}
      <div className="grid grid-cols-4 gap-2">
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-3">
          <p className="text-xs text-slate-400 font-medium">Total</p>
          <p className="text-xl font-bold text-slate-800 mt-1">{summary.total_records}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-3">
          <p className="text-xs text-slate-400 font-medium">Avg Score</p>
          <p className={`text-xl font-bold mt-1 ${summary.average_score >= 80 ? "text-green-600" : "text-amber-600"}`}>{summary.average_score}%</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-3">
          <p className="text-xs text-slate-400 font-medium">Poor &lt;60%</p>
          <p className="text-xl font-bold text-red-600 mt-1">{summary.poor_records}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-3">
          <p className="text-xs text-slate-400 font-medium">Good 80%+</p>
          <p className="text-xl font-bold text-green-600 mt-1">{summary.good_records}</p>
        </div>
      </div>

      <p className="text-xs text-slate-400 font-medium px-1">Click a record to fill missing fields inline:</p>

      {/* Records List */}
      <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
        {records.slice(0, 30).map((r) => {
          const isExpanded = expandedId === r.business_id;
          const barColor = r.completeness_score >= 80 ? "bg-green-400" : r.completeness_score >= 60 ? "bg-amber-400" : "bg-red-400";
          const editableFields = (r.missing_fields || []).filter((mf) => FIELD_CONFIG[mf.field]);

          return (
            <div key={r.business_id} className={`rounded-xl border transition ${isExpanded ? "border-sky-200 bg-sky-50/30" : "border-slate-100 bg-slate-50"}`} data-testid={`dc-row-${r.business_id}`}>
              <button
                onClick={() => setExpandedId(isExpanded ? null : r.business_id)}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
                data-testid={`dc-toggle-${r.business_id}`}
              >
                <div className="w-8 h-2 rounded-full bg-slate-200 overflow-hidden flex-shrink-0">
                  <div className={`h-full rounded-full ${barColor}`} style={{ width: `${r.completeness_score}%` }} />
                </div>
                <span className="text-xs font-bold text-slate-600 w-10">{r.completeness_score}%</span>
                <span className="text-xs text-slate-700 flex-1 truncate">{r.name}</span>
                <span className="text-[10px] text-slate-400">{r.missing_count} missing</span>
                {saving === r.business_id ? (
                  <Loader2 className="w-3 h-3 animate-spin text-sky-500 flex-shrink-0" />
                ) : isExpanded ? (
                  <ChevronDown className="w-3 h-3 text-slate-400 flex-shrink-0" />
                ) : (
                  <ChevronRight className="w-3 h-3 text-slate-400 flex-shrink-0" />
                )}
              </button>

              {isExpanded && editableFields.length > 0 && (
                <div className="px-3 pb-3 pt-1 border-t border-slate-100 space-y-2" data-testid={`dc-editor-${r.business_id}`}>
                  <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Missing Fields — Click to fill</p>
                  <div className="flex flex-wrap gap-1.5">
                    {editableFields.map((mf) => (
                      <InlineEdit
                        key={mf.field}
                        field={mf.field}
                        config={FIELD_CONFIG[mf.field]}
                        onSave={(field, value) => handleFieldSave(r.business_id, r.name, field, value)}
                      />
                    ))}
                  </div>
                  {/* Show tier breakdown */}
                  <div className="flex gap-3 mt-1">
                    {["required", "important", "bonus"].map((tier) => {
                      const count = editableFields.filter((f) => f.tier === tier).length;
                      if (count === 0) return null;
                      const tierColor = tier === "required" ? "text-red-500" : tier === "important" ? "text-amber-500" : "text-slate-400";
                      return (
                        <span key={tier} className={`text-[9px] font-medium ${tierColor}`}>
                          {count} {tier}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
